import { Button, Toolbar, Typography } from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { useFormContext, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { NavBarButtonGroup } from "~/features/navbar/Navbar";
import { WorkstationSettingsFormValues } from "~/features/preview/workstation/hooks/useWorkstationConfigForm";

import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { updateSelectedSiteWorkstation } from "~/redux/actions/workstations";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import {
  usePostWorkstationPickingConfigurationMutation,
  usePutWorkstationMutation
} from "~/redux/warehouse/workstation.hooks";
import {
  UpdateWorkstation,
  WorkstationSummaryDto,
  TotePlacementSummaryDto
} from "~/types/api";

type WorkstationChangesToolbarProps = {
  onDismissToolbar: (nextValues?: WorkstationSettingsFormValues) => void;
};

function toUpdateWorkstation(
  formValues: WorkstationSettingsFormValues
): UpdateWorkstation {
  const { temperatureZones: _temperatureZones, ...workstation } = formValues;
  return workstation;
}

export const WorkstationChangesToolbar = (
  props: WorkstationChangesToolbarProps
) => {
  const { onDismissToolbar } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const trackedPageName = useMixpanelPageName();
  const selectedSiteWorkstation = useAppSelector(selectThisWorkstation);
  const { errorToast, successToast } = useToast();
  const [putWorkstation, { isLoading: isSavingWorkstation }] =
    usePutWorkstationMutation();
  const [postPickingConfiguration, { isLoading: isSavingPickingConfig }] =
    usePostWorkstationPickingConfigurationMutation();

  const { control, getValues } =
    useFormContext<WorkstationSettingsFormValues>();
  const { errors, isDirty } = useFormState({ control });

  const hasBlockingErrors = Object.keys(errors).length > 0;
  const isSaving = isSavingWorkstation || isSavingPickingConfig;
  const shouldShowSaveBtn = isDirty && !hasBlockingErrors;

  const handlePutRequest = async (
    updatedWorkstation: UpdateWorkstation
  ): Promise<boolean> => {
    try {
      await putWorkstation(updatedWorkstation).unwrap();
      if (updatedWorkstation.workstationId === selectedSiteWorkstation?.id) {
        const {
          autostoreGridId: gridId,
          deviceId,
          mode,
          multiPortEnabled,
          orientation,
          pickableOrderTypes,
          ports,
          roles,
          totePlacements
        } = updatedWorkstation;

        dispatch(
          updateSelectedSiteWorkstation({
            ...selectedSiteWorkstation,
            autostoreGridId: gridId,
            deviceId,
            multiPortEnabled,
            orientation,
            pickableOrderTypes,
            ports,
            roles,
            totePlacements: totePlacements.map(
              (p): TotePlacementSummaryDto => ({
                coordinate: p.coordinate,
                putToLightModuleId: p.putToLightModuleId,
                totePosition: p.totePosition
              })
            ),
            mode: mode as WorkstationSummaryDto["mode"]
          })
        );
      }
      return true;
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
      return false;
    }
  };

  const handlePostPickingConfiguration = async (
    formValues: WorkstationSettingsFormValues
  ): Promise<boolean> => {
    const {
      autostoreGridId: gridId,
      workstationId,
      pickableOrderTypes,
      temperatureZones
    } = formValues;
    if (!gridId || !workstationId) {
      // gridId and workstationId are expected, but if not, return true so save still runs (false only means POST failed).
      return true;
    }
    try {
      await postPickingConfiguration({
        autostoreGridId: gridId,
        workstationId,
        body: {
          orderTypes: pickableOrderTypes ?? [],
          temperatureZones: temperatureZones ?? []
        }
      }).unwrap();
      return true;
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
      return false;
    }
  };

  const handleSaveChanges = async () => {
    // Manually checking isValid here instead of using formState.isValid
    // because formState.isValid doesn't work with setError
    const isValid = Object.keys(errors).length === 0;
    if (!isValid) {
      return errorToast(t("please correct the errors on the form"));
    }
    const formValues = getValues();
    mixpanelTrack({
      trackedPageName,
      type: "Button Click",
      label: "Save Workstation Changes"
    });
    const trimmedFormValues: WorkstationSettingsFormValues = {
      ...formValues,
      deviceId: formValues.deviceId.trim()
    };
    // Picking-config first so a rejected POST does not leave workstation PUT applied.
    const savedPickingConfig =
      await handlePostPickingConfiguration(trimmedFormValues);
    if (!savedPickingConfig) {
      return;
    }
    const savedWorkstation = await handlePutRequest(
      toUpdateWorkstation(trimmedFormValues)
    );
    if (!savedWorkstation) {
      return;
    }
    successToast(t("workstation saved"));
    onDismissToolbar(trimmedFormValues);
  };

  return (
    <Toolbar>
      <NavBarButtonGroup>
        {shouldShowSaveBtn && (
          <Button
            color="secondary"
            onClick={handleSaveChanges}
            disabled={isSaving}
          >
            <Typography variant="body2" sx={{ color: "common.white" }}>
              {t("save changes")}
            </Typography>
          </Button>
        )}
        <Button
          color="secondary"
          onClick={() => {
            mixpanelTrack({
              trackedPageName,
              type: "Button Click",
              label: "Cancel Workstation Changes"
            });
            onDismissToolbar();
          }}
          disabled={isSaving}
        >
          <Typography variant="body2" sx={{ color: "common.white" }}>
            {t("cancel")}
          </Typography>
        </Button>
      </NavBarButtonGroup>
    </Toolbar>
  );
};
